import { clsx } from 'clsx';
import moment from 'moment';
import Papa from 'papaparse';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';
import * as XLSX from 'xlsx';
import { logger } from './logger';

export const cn = (...inputs) => {
  return twMerge(clsx(inputs));
};

/**
 * Handles the API response
 * @param {promise} api_call
 * @param {string|boolean} toast_success
 * @param {string|boolean} toast_loading de
 * @returns response data or null and show error / success alerts.
 */
export const responseHandler = async (api_call, toast_success = false, toast_loading = false) => {
  let response = null;
  // If loading and success message is provided then show promise toast message.
  const toastId = toast;
  if (toast_loading) toast.loading(toast_loading, { id: toastId });

  try {
    response = await api_call;

    if (!response?.data?.status) {
      response.toastId = toastId;
      toast.error(response?.data?.message, { id: toastId });
    } else {
      if (toast_success) toast.success(toast_success, { id: toastId });
    }
  } catch (e) {
    response = e;
  }
  logger.debug('STATUS CODE : ' + response?.status_code, { context: 'responseHandler', response });

  // Handle success / error response
  if (response.status_code == 200 || response.status_code == 201) {
    if (toast_success) toast.success(toast_success, { id: toastId });
    return response?.data;
  } else if (response?.status_code == 400)
    toast.error('Error 400 : ' + response?.data?.message, { id: toastId });
  else if (response?.status_code == 401)
    toast.error(
      `Unauthorized 401 : ${response?.data?.message ? response?.data?.message : 'Action is not permitted.'}`,
      { id: toastId }
    );
  else if (response?.status_code == 403)
    toast.error('Unauthorized 403 : Action forbidden.', { id: toastId });
  else if (response.status_code === 422) {
    const msg = response?.data?.message || 'Validation error';
    toast.error('Error 422 : ' + msg, { id: toastId });
    return response.data;
  } else if (response?.status_code === 500)
    toast.error('Error 500 : ' + response?.data?.message, { id: toastId });
  else toast.error('Error : Something went wrong. Please contact admin.', { id: toastId });
  return response;
};

/**
 * Generate random string along with current length
 * @param {Number} length
 * @returns {string}
 */
export const generateString = (length) => {
  var uniStr = '';
  const str = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let i = 0;
  while (i <= length) {
    let num = Math.floor(Math.random() * 62);
    uniStr += str.slice(num - 1, num);
    i++;
  }
  return uniStr;
};

/**
 * Convert ISO timestamp to human readable format
 * @param {string|Date} dateTime - ISO string or Date object
 * @param {string} format - Desired format (default: 'MMMM Do YYYY, h:mm:ss A')
 * @returns {string} formatted date
 *
 * @example
 * formatDate('2025-08-16T08:22:55.425404+00:00');
 * // "August 16th 2025, 1:52:55 PM"
 */
export const formatDate = (dateTime, format = 'MMM, Do YYYY') => {
  return moment(dateTime).format(format);
};

/**
 * Convert ms to hours
 * @param {string|Date} dateTime - ISO string or Date object
 * @param {string} format - Desired format (default: 'MMMM Do YYYY, h:mm:ss A')
 * @returns {string} formatted date
 *
 */
export const msToHours = (ms, format = 'decimal') => {
  const duration = moment.duration(ms);

  if (format === 'text') {
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  if (format === 'clock') {
    return moment.utc(ms).format('HH:mm:ss');
  }

  return duration.asHours();
};

/**
 * Calculates the duration between two timestamps
 * @param {string|Date} startTime - Start time.
 * @param {string|Date} endTime - End time.
 * @returns {string} Formatted duration (e.g., "1h 23m 45s").
 */
export function calculateDuration(startTime, endTime) {
  if (!startTime || !endTime) return '-';

  const start = moment(startTime);
  const end = moment(endTime);

  const duration = moment.duration(end.diff(start));

  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Format large numbers to show in hundreds or millions
 * @param {string|Date} dateTime - ISO string or Date object
 * @param {string} format - Desired format (default: 'MMMM Do YYYY, h:mm:ss A')
 * @returns {string} formatted date
 *
 */
export const formatNumber = (num) => {
  if (num == null || isNaN(num)) return '0';
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + ' B';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + ' M';

  return num.toLocaleString('en-US');
};

export function formatTokens(value) {
  if (value == null || isNaN(value)) return "0";

  if (value >= 1_000_000_000) {
    return (value / 1_000_000_000).toFixed(2) + "B";
  }

  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(2) + "M";
  }

  if (value >= 1_000) {
    return (value / 1_000).toFixed(2) + "K";
  }

  return value.toString();
}

export function formatTime(seconds) {
  const hrs = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");

  const mins = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");

  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");

  return `${hrs}:${mins}:${secs}`;
}


/**
 * Convert a JSON object into FormData
 * Handles nested objects, arrays, and files
 *
 * @param {Object} data - JSON data to convert
 * @param {FormData} [formData] - Optional existing FormData
 * @param {string} [parentKey] - Internal key prefix for recursion
 * @returns {FormData}
 *
 * @example
 * const data = {
 *   name: "John",
 *   age: 30,
 *   address: { city: "NY", zip: "10001" },
 *   hobbies: ["reading", "sports"],
 *   profile: fileInput.files[0], // File object
 * };
 *
 * const formData = jsonToFormData(data);
 */
export const jsonToFormData = (data, formData = new FormData(), parentKey = '') => {
  if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
    Object.keys(data).forEach((key) => {
      const value = data[key];
      const newKey = parentKey ? `${parentKey}[${key}]` : key;

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          jsonToFormData(item, formData, `${newKey}[${index}]`);
        });
      } else if (value && typeof value === 'object') {
        jsonToFormData(value, formData, newKey);
      } else {
        formData.append(newKey, value ?? '');
      }
    });
  } else {
    formData.append(parentKey, data ?? '');
  }

  return formData;
};

/**
 * Convert string to slug format
 * @returns string
 */
export const slugify = (str) => {
  str = str.replace(/^\s+|\s+$/g, ''); // trim leading/trailing white space
  str = str.toLowerCase(); // convert string to lowercase
  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-'); // remove consecutive hyphens
  return str;
};
/**
 * Convert string to slug format
 * @returns string
 */
export const slugifyKey = (str) => {
  str = str.replace(/^\s+|\s+$/g, ''); // trim leading/trailing white space
  str = str.toLowerCase(); // convert string to lowercase
  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
    .replace(/\s+/g, '_') // replace spaces with hyphens
    .replace(/-+/g, '_'); // remove consecutive hyphens
  return str;
};

/**
 * Given a status string, returns the corresponding variant string to use for UI components (e.g. Button, Badge, etc.).
 * @param {string} status The status string to map to a variant.
 * @returns {string} The corresponding variant string ('default', 'secondary', 'destructive', or 'outline').
 */
export const getStatusVariant = (status) => {
  switch (status) {
    case 'active':
      return 'default';
    case 'inactive':
      return 'secondary';
    case 'expired':
      return 'destructive';
    case 'draft':
      return 'secondary';
    default:
      return 'outline';
  }
};

/**
 * Put error message in react-hook-form errors
 * @param {form}  React-hook-form instance
 * @param {array} errors
 * @returns
 */
export const computeErrors = (form, errors) => {
  return errors?.map((err) => {
    form.setError(err.field, { message: err.message, type: 'manual' });
  });
};

export const sliceLongText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + '...';
  }
  return text;
};

/**
 * Creates an array of dynamic fields based on the provided variables.
 * Each field is an object containing the following properties:
 * - type: The type of the field (text or select).
 * - name: The name of the field.
 * - label: The label to display for the field.
 * - placeholder: The placeholder text to display for the field.
 * - required: A boolean indicating whether the field is required.
 * - options: An array of objects containing the label and value for each option, if the field is of type select.
 * @param {Array} variables - An array of objects containing the properties of the variables to create dynamic fields for.
 * @returns {Array} An array of dynamic fields.
 */
export const createDynamicFields = (variables) => {
  if (!Array.isArray(variables)) return [];
  return variables.map((variable) => {
    const promptBlockVariableMapId = variable?.prompt_block_variable_map?.[0]?.id ?? null;
    if (variable.type === 'text') {
      return {
        type: 'text',
        name: variable.key,
        label: variable.name,
        placeholder: variable.description || '',
        required: true,
        promptBlockVariableMapId,
        variableId: variable.id,
      };
    }

    if (variable.type === 'selection') {
      return {
        type: 'select',
        name: variable.key,
        label: variable.name,
        required: true,
        options: variable.options.map((opt) => ({
          label: opt.name,
          value: opt.key,
        })),
        promptBlockVariableMapId,
        variableId: variable.id,
      };
    }

    return null;
  });
};


export const formatDefaultValues = (values) => {
  if (!Array.isArray(values)) return [];
  return values.map((value) => {
    return {
      field_name: value.variable.key,
      field_value: value.value,
      field_sequence: value.sequence,
      canUserEdit: value.can_user_edit
    };
  });
}


/**
 *  Parses a file based on its extension and returns the parsed data or an error message.
 * @file {File} file - The file should be of type CSV or Excel to be parsed.
 */
const normalizeEmails = (rows) =>
  rows?.map((row) => {
    const normalized = { ...row };
    if (normalized.Email) normalized.Email = normalized.Email.toLowerCase();
    if (normalized.email) normalized.email = normalized.email.toLowerCase();
    return normalized;
  });

export const parseFile = async (file) => {
  const extension = file.name.split('.').pop().toLowerCase();

  if (extension === 'csv') {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => resolve(normalizeEmails(result.data)),
        error: (error) => reject(error),
      });
    });
  } else if (['xlsx', 'xls', 'xlsm', 'xlsb'].includes(extension)) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        resolve(normalizeEmails(json));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  } else {
    return null;
  }
};
